import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import {
  DECODE_LAST_FRAME,
  DECODE_TICK_MS,
  decodeDelay,
  decodeFrame,
  FOCUS_DELAY_MS,
  isCurrentRow,
  summonKeyAction,
  wrapIndex,
  type SummonEntry,
  type SummonRow,
} from './summon';

/** Production read the motion preference once, when the script ran; so does the rebuild. */
const REDUCED_MOTION_AT_START =
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface SummonOverlayProps {
  readonly open: boolean;
  /** Increments on every open; each open repaints the rows and restarts the decode. */
  readonly session: number;
  readonly entries: readonly SummonEntry[];
  readonly pathname: string;
  onClose: () => void;
}

function rowsOf(entries: readonly SummonEntry[]): SummonRow[] {
  return entries.flatMap((entry) => (entry.kind === 'row' ? [entry.row] : []));
}

/**
 * The modal navigation dialog. Markup and classes are production's (ui-layer.md §1.2 build/paint);
 * behaviour follows UI-R3: focus moves in and is trapped, Esc and the backdrop close, the page
 * does not scroll behind it, rows are unreachable while closed, the current page is marked.
 * Rows are plain links: choosing one loads that document.
 */
export function SummonOverlay({ open, session, entries, pathname, onClose }: SummonOverlayProps) {
  const veil = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const rowElements = useRef<(HTMLAnchorElement | null)[]>([]);
  const rows = rowsOf(entries);
  const [cursor, setCursor] = useState(0);
  const cursorRef = useRef(0);
  const [labels, setLabels] = useState<readonly string[]>(() =>
    rows.map((row) => (REDUCED_MOTION_AT_START ? row.label : '')),
  );

  const focusCurrent = useCallback(() => {
    const row = rowElements.current[cursorRef.current];
    if (row) row.focus();
    else list.current?.focus();
  }, []);

  const select = useCallback(
    (index: number) => {
      const count = rowsOf(entries).length;
      if (count === 0) return;
      const next = wrapIndex(index, count);
      cursorRef.current = next;
      setCursor(next);
      const row = rowElements.current[next];
      if (row) {
        row.scrollIntoView({ block: 'nearest' });
        row.focus();
      }
    },
    [entries],
  );

  // Every open: selection back to the first row, labels decode, focus lands on row 0.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const current = rowsOf(entries);
    cursorRef.current = 0;
    setCursor(0);
    const timers: number[] = [];
    if (REDUCED_MOTION_AT_START) {
      setLabels(current.map((row) => row.label));
    } else {
      setLabels(current.map(() => ''));
      current.forEach((row, index) => {
        timers.push(
          window.setTimeout(() => {
            let frame = 0;
            const tick = window.setInterval(() => {
              const done = frame >= DECODE_LAST_FRAME;
              const text = done ? row.label : decodeFrame(row.label, frame);
              frame += 1;
              setLabels((previous) => previous.map((value, i) => (i === index ? text : value)));
              if (done) window.clearInterval(tick);
            }, DECODE_TICK_MS);
            timers.push(tick);
          }, decodeDelay(index)),
        );
      });
    }
    timers.push(window.setTimeout(focusCurrent, FOCUS_DELAY_MS));
    return () => {
      timers.forEach((timer) => {
        window.clearTimeout(timer);
        window.clearInterval(timer);
      });
    };
  }, [open, session, entries, focusCurrent]);

  // Closed: out of the tab order and the accessibility tree (UI-R3).
  useLayoutEffect(() => {
    const element = veil.current;
    if (element !== null) element.inert = !open;
  }, [open]);

  // Keyboard and focus containment while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      const action = summonKeyAction(event, true);
      if (action === null || action === 'toggle') return; // toggle is owned by the nav
      if (action === 'activate') {
        const row = rowElements.current[cursorRef.current];
        if (!row) return;
        event.preventDefault();
        row.click();
        return;
      }
      event.preventDefault();
      if (action === 'close') onClose();
      else if (action === 'next') select(cursorRef.current + 1);
      else if (action === 'previous') select(cursorRef.current - 1);
      else focusCurrent();
    };
    const onFocusIn = (event: FocusEvent) => {
      if (veil.current !== null && event.target instanceof Node && !veil.current.contains(event.target)) {
        focusCurrent();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [open, onClose, select, focusCurrent]);

  // The page behind does not scroll while the dialog is open.
  useEffect(() => {
    if (!open) return undefined;
    const { body, documentElement } = document;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;
    const scrollbar = window.innerWidth - documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
    };
  }, [open]);

  function onBackdrop(event: MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) onClose();
  }

  rowElements.current.length = rows.length;

  return (
    <div
      ref={veil}
      className="omega-veil"
      role="dialog"
      aria-modal="true"
      aria-label="Navigation"
      aria-hidden={open ? undefined : true}
      data-open={open ? '1' : '0'}
      onClick={onBackdrop}
    >
      <div className="omega-veil__mark" aria-hidden="true"></div>
      <div className="omega-panel">
        <ul ref={list} className="omega-list" role="listbox" tabIndex={-1}>
          {entries.map((entry) =>
            entry.kind === 'separator' ? (
              <li key="separator" className="omega-sep" aria-hidden="true"></li>
            ) : (
              <li key={entry.row.href} role="presentation">
                <a
                  ref={(element) => {
                    rowElements.current[entry.index] = element;
                  }}
                  className={entry.row.unforged ? 'omega-row omega-row--unforged' : 'omega-row'}
                  href={entry.row.href}
                  role="option"
                  tabIndex={entry.index === cursor ? 0 : -1}
                  aria-selected={entry.index === cursor}
                  aria-current={isCurrentRow(entry.row.href, pathname) ? 'page' : undefined}
                  aria-label={entry.row.label}
                  onMouseEnter={() => {
                    select(entry.index);
                  }}
                >
                  {entry.row.glyphHtml === undefined ? (
                    <span className="omega-row__g">{entry.row.glyph}</span>
                  ) : (
                    <span className="omega-row__g" dangerouslySetInnerHTML={{ __html: entry.row.glyphHtml }} />
                  )}
                  <span className="omega-row__l">{labels[entry.index] ?? ''}</span>
                </a>
              </li>
            ),
          )}
        </ul>
        <p className="omega-hint omega-hint--keys">
          <b>↑↓</b> move &nbsp;·&nbsp; <b>⏎</b> go &nbsp;·&nbsp; <b>esc</b> dismiss
        </p>
        <p className="omega-hint omega-hint--touch">tap anywhere outside to dismiss</p>
      </div>
    </div>
  );
}
